import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Calendar,
  AlertCircle,
  Plus
} from 'lucide-react';
import { getInboxMessages } from '../../api/wholesale/whatsapp';
import { createOrderFromWhatsApp } from '../../api/wholesale/orders';
import { getDistributors } from '../../api/wholesale/distributors';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const WhatsAppOrdersPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [distributors, setDistributors] = useState([]);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getInboxMessages({
        status: statusFilter !== 'all' ? statusFilter : undefined
      });
      const data = response?.data || response || [];
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching WhatsApp messages:', err);
      setError('Failed to load WhatsApp messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await getDistributors();
      const data = response?.data || response || [];
      setDistributors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching distributors:', err);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchDistributors();
  }, [statusFilter]);

  const handleCreateOrder = async (message, selectedDistributorId) => {
    setIsProcessing(true);
    try {
      const orderData = {
        whatsapp_order_id: message.id,
        distributor_id: selectedDistributorId || message.distributor_id,
        items: message.parsed_items || [],
        notes: message.message_text
      };

      await createOrderFromWhatsApp(message.id, orderData);

      // Refresh messages
      await fetchMessages();
      setShowOrderModal(false);
      setSelectedMessage(null);

      alert('Order created successfully!');
    } catch (err) {
      console.error('Error creating order:', err);
      alert(err.message || 'Failed to create order from WhatsApp message');
    } finally {
      setIsProcessing(false);
    }
  };

  const reviewStatusConfig = {
    pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
    spam: { label: 'Spam', color: 'bg-gray-100 text-gray-700', icon: XCircle }
  };

  const parsingStatusConfig = {
    success: { label: 'Parsed', color: 'bg-green-100 text-green-700' },
    partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700' },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
    manual: { label: 'Manual', color: 'bg-blue-100 text-blue-700' }
  };

  const tabs = [
    { id: 'all', label: 'All Messages' },
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' }
  ];

  const filteredMessages = messages.filter((message) => {
    const senderPhone = message.sender_phone || '';
    const distributorName = message.distributor_name || '';
    const messageText = message.message_text || '';

    return (
      senderPhone.includes(searchQuery) ||
      distributorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      messageText.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  if (loading && messages.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Orders Inbox</h1>
          <p className="text-gray-500 mt-1">Review and process incoming WhatsApp orders</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${
                  statusFilter === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by phone, distributor, or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Messages List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredMessages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No WhatsApp messages found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredMessages.map((message) => {
              const reviewStatus =
                reviewStatusConfig[message.review_status] || reviewStatusConfig.pending;
              const parsingStatus =
                parsingStatusConfig[message.parsing_status] || parsingStatusConfig.manual;
              const ReviewIcon = reviewStatus.icon;

              return (
                <div key={message.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <MessageSquare className="h-5 w-5 text-emerald-600" />
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{message.sender_phone}</span>
                        </div>
                        {message.distributor_name && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {message.distributor_name}
                            </span>
                            {message.distributor_number && (
                              <span className="text-xs text-gray-500">
                                ({message.distributor_number})
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 ml-auto">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            {new Date(message.received_at).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-3">
                        <p className="text-gray-700 whitespace-pre-wrap">{message.message_text}</p>
                      </div>

                      {/* Parsed Items */}
                      {message.parsed_items && message.parsed_items.length > 0 && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Parsed Items:</h4>
                          <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                            {message.parsed_items.map((item, idx) => (
                              <div key={idx} className="text-sm text-gray-700">
                                <span className="font-medium">{item.tile_code || 'Unknown'}</span> -
                                Qty: {item.quantity || 0}
                                {item.confidence_score && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({Math.round(item.confidence_score * 100)}% confidence)
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Status Badges */}
                      <div className="flex items-center gap-3">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${reviewStatus.color}`}
                        >
                          {ReviewIcon && <ReviewIcon className="h-3 w-3" />}
                          {reviewStatus.label}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${parsingStatus.color}`}
                        >
                          {parsingStatus.label}
                        </span>
                        {message.order_created && message.order_number && (
                          <Link
                            to={`/wholesale/orders/${message.wholesale_order_id}`}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium hover:bg-green-200 transition-colors"
                          >
                            <CheckCircle className="h-3 w-3" />
                            Order Created: {message.order_number}
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {!message.order_created && message.review_status === 'pending' && (
                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={() => {
                            setSelectedMessage(message);
                            setShowOrderModal(true);
                          }}
                          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Create Order
                        </button>
                        <button className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                          Mark as Spam
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Order Modal */}
      {showOrderModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Create Order from WhatsApp</h2>

              <div className="space-y-4">
                {/* Distributor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Distributor
                  </label>
                  <select
                    id="distributor-select"
                    defaultValue={selectedMessage.distributor_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">-- Select Distributor --</option>
                    {distributors.map((dist) => (
                      <option key={dist.id} value={dist.id}>
                        {dist.business_name} ({dist.distributor_number})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original Message
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedMessage.message_text}
                    </p>
                  </div>
                </div>

                {/* Parsed Items Preview */}
                {selectedMessage.parsed_items && selectedMessage.parsed_items.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Suggested Items (You can edit in the next step)
                    </label>
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                      {selectedMessage.parsed_items.map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-700">
                          {item.tile_code || 'Unknown'} - Qty: {item.quantity || 0}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium mb-1">Note:</p>
                      <p>
                        The order will be created with the parsed items above. You can review and
                        edit the order details after creation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setSelectedMessage(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const distributorId = document.getElementById('distributor-select').value;
                    if (!distributorId) {
                      alert('Please select a distributor');
                      return;
                    }
                    handleCreateOrder(selectedMessage, parseInt(distributorId));
                  }}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isProcessing ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppOrdersPage;
